import { BRANCHING_PARAMETER_KEYS } from 'pages/Flow/constants';
import { FLOW_RUN_STATUS, runStatus } from 'utils/ignoramusEnums';


export const hasNodeListBeenModified = (initialNodeList, latestNodeList) => latestNodeList.filter(
  latestNode => {
    const currentNode = initialNodeList.find(node => node.ID === latestNode.id);

    // When a new node is added to the flow, add it as modified
    if (!currentNode) {
      // Object is not present in the initialNodeList, so it was added
      return true;
    }

    // When an existing flow node is updated, add it as modified
    const isPositionXModified = latestNode.position.x !== currentNode.positionX;
    const isPositionYModified = latestNode.position.y !== currentNode.positionY;
    const isReferenceIDModified = latestNode.data.referenceID !== currentNode.referenceID;
    const isTypeModified = latestNode.type !== currentNode.nodeType;
    const isLabelModified = latestNode.data.label !== currentNode.label;
    // Check tags - normalize to empty string to detect when tags are cleared
    const isTagsModified = (latestNode.data.tags || '') !== (currentNode.tags || '');

    return isPositionXModified || isPositionYModified || isReferenceIDModified
      || isTypeModified || isLabelModified || isTagsModified;
  },
);

export const isValidDeltaExponential = (value) => {
  if (!value) {
    return false;
  }

  const pattern = /^(\d*\.?\d+)E([-+]?\d+)$/;
  const match = value.match(pattern);

  if (!match) {
    return false;
  }

  const base = parseFloat(match[1]);
  const exponent = parseInt(match[2], 10);

  const number = base * 10 ** exponent;
  const lowerBound = 1e-10;
  const upperBound = 1e-5;
  const epsilon = 1e-15; // Small margin of error

  // Ensure comparison accounts for floating-point precision
  return number >= lowerBound - epsilon && number <= upperBound + epsilon;
};

export const getDeltaString = (param) => {
  const defaultValue = '1.4E-10';

  if (isValidDeltaExponential(param)) {
    return param;
  }
  else {
    return defaultValue;
  }
};

/**
 * Extracts the ID from a string in the format "Level-{ID}"
 * @param {string} levelString - String in format "Level-{ID}"
 * @returns {number|null} - The extracted ID or null if invalid format
 */
export const extractLevelId = (levelString) => {
  if (!levelString || typeof levelString !== 'string') {
    return null;
  }
  const levelId = Number(levelString.replace('Level-', ''));
  return Number.isNaN(levelId) ? null : levelId;
};

/**
 * Finds the first Run Time Parameter that contains branching-related information
 * @param {Array} runParameters - Array of RTPs objects, each containing a 'parameters' property
 * @returns {Object|null} - The parameter object containing branching keys, or null if none found
 */
export const findBranchingParameter = (runParameters) => {
  if (!runParameters || runParameters.length === 0) {
    return null;
  }

  return runParameters.find(({ parameters }) => (
    parameters && BRANCHING_PARAMETER_KEYS.some((key) => key in parameters)
  ));
};

/**
 * Finds all parameters in cached run data that contain branching-related information
 * @param {Array} runParameters - Array of cached run parameter objects, each with a 'name' property
 * @returns {Array} - List of parameters with branching-related names, or empty array if none found
 */
export const findBranchingParametersInCachedRun = (runParameters) => {
  if (!runParameters || runParameters.length === 0) {
    return [];
  }

  return runParameters.filter(({ name }) => (
    name && BRANCHING_PARAMETER_KEYS.includes(name)
  ));
};

/**
 * Checks if a parameter object from a request payload is a global (flow-level) branching parameter.
 * This is used to filter these parameters out of the payload before sending it, as they are
 * handled separately from node-specific parameters.
 *
 * @param {Object} parameter - A parameter object with a nested 'parameters' property.
 * @returns {boolean} - True if the parameter represents a global branching setting.
 */
export const isGlobalBranchingParameter = (parameter) => {
  if (!parameter || !parameter.parameters) {
    return false;
  }

  return BRANCHING_PARAMETER_KEYS.some(key => key in parameter.parameters) && parameter.nodeID === '';
};


/**
 * Checks if a parameter from a saved "base run" is a global branching parameter.
 * The UI uses this to identify and auto-populate branching-related input fields
 * when creating a new run based on a previous one.
 *
 * @param {Object} parameter - A parameter object from a base run, with a top-level 'name' property.
 * @returns {boolean} - True if the parameter from the run is a global branching setting.
 */
export const isGlobalBranchingParameterFromRun = (parameter) => {
  if (!parameter || !parameter.name) {
    return false;
  }

  return BRANCHING_PARAMETER_KEYS.includes(parameter.name) && parameter.nodeID === '';
};

/**
 * Generates a tooltip message for run status with elapsed time
 * @param {string} status - The run status key (e.g., 'RUNNING', 'COMPLETED', 'FAILED', 'COMPLETE')
 * @param {string|null} elapsedTime - The elapsed time string (e.g., '2h 15m 30s')
 * @returns {string|null} - The tooltip message or null if elapsedTime is missing
 */
export const getRunStatusTooltipMessage = (status, elapsedTime) => {
  // Don't show tooltip if elapsedTime is missing
  if (!elapsedTime) {
    return null;
  }

  if (status === runStatus.RUNNING.key) {
    return `Running for ${elapsedTime}`;
  }

  if (status === runStatus.COMPLETED.key || status === runStatus.COMPLETE.key) {
    return `Completed in ${elapsedTime}`;
  }

  if (status === runStatus.FAILED.key) {
    return `Failed in ${elapsedTime}`;
  }

  return null;
};

/**
 * Maps run status (flow or node) to Status component props (color and label)
 * Supports both flow run statuses and node run statuses
 * @param {string|null|undefined} status - The run status key
 * @returns {Object|null} - Object with color and label properties, or null if status not supported
 */
// Map status keys to Status component colors (Temporal-standard)
const STATUS_COLOR_MAP = {
  COMPLETED: 'success',
  COMPLETE: 'success',
  FAILED: 'error',
  TIMED_OUT: 'error',
  RUNNING: 'info', // Blue - active execution
  QUEUED: 'warning', // Yellow - queued state
  FLOW_PAUSED: 'info', // Purple/pending (mapped to info)
  TERMINATED: 'disabled', // Gray - neutral termination
  CONTINUED_AS_NEW: 'info', // Blue - active execution
  CANCELED: 'disabled', // Gray - neutral termination
  CANCELING: 'warning', // Transitional state
  RUN_STATUS_UNKNOWN: 'disabled',
};

export const getRunStatusProps = (status) => {
  if (!status) return null;

  // Try FLOW_RUN_STATUS first (has more statuses), then fall back to runStatus
  const flowStatus = Object.values(FLOW_RUN_STATUS).find(s => s.key === status);
  const nodeStatus = Object.values(runStatus).find(s => s.key === status);
  const statusInfo = flowStatus || nodeStatus;

  if (!statusInfo) return null;

  const color = STATUS_COLOR_MAP[status] || 'default';

  return { color, label: statusInfo.label };
};

/**
 * Maps flow run status to Status component color
 * @param {string|null|undefined} status - The flow run status key
 * @returns {string} - The Status component color ('success', 'error', 'info', 'warning', 'default')
 */
export const getFlowRunStatusColor = (status) => {
  const props = getRunStatusProps(status);
  return props?.color || 'default';
};

